import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    CalendarIcon,
    ChartBarIcon,
  CogIcon, 
    MagnifyingGlassIcon,
    PencilIcon,
    PlusIcon,
  TrashIcon, 
    UserGroupIcon,
    FireIcon,
    RectangleStackIcon
  } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import TabNavigation from '../components/TabNavigation';
import clsx from 'clsx';

interface Workout {
  id: number;
  name: string;
  description?: string;
  instructions?: string;
  category_id?: number;
  category_name?: string;
  category_color?: string;
  duration_minutes?: number;
  equipment_needed?: string[];
  muscle_groups?: string[];
  video_url?: string;
  image_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface WorkoutCategory {
  id: number;
  name: string;
  color: string;
  is_active: boolean;
}

interface WorkoutProgram {
  id: number;
  name: string;
  description?: string;
  duration_weeks: number;
  created_at: string;
  updated_at: string;
}

const WorkoutsPage: React.FC = () => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [categories, setCategories] = useState<WorkoutCategory[]>([]);
  const [programs, setPrograms] = useState<WorkoutProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [showCreateWorkout, setShowCreateWorkout] = useState(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showCreateProgram, setShowCreateProgram] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState<{ type: 'workout' | 'category' | 'program'; id: number; name: string } | null>(null);
  const [editingWorkout, setEditingWorkout] = useState<Workout | null>(null);
  const [viewingWorkout, setViewingWorkout] = useState<Workout | null>(null);
  const [editingCategory, setEditingCategory] = useState<WorkoutCategory | null>(null);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [activeTab, setActiveTab] = useState<'workouts' | 'categories' | 'programs'>('workouts');

  const [newWorkout, setNewWorkout] = useState({
    name: '',
    description: '',
    instructions: '',
    category_id: null as number | null,
    duration_minutes: null as number | null,
    equipment_needed: [] as string[],
    muscle_groups: [] as string[],
    video_url: '',
    image_url: ''
  });

  const [newCategory, setNewCategory] = useState({
    name: '',
    color: '#6366f1'
  });

  const [newProgram, setNewProgram] = useState({
    name: '',
    description: '',
    duration_weeks: 4
  });

  const [programWorkouts, setProgramWorkouts] = useState<Record<number, Array<{
    id: string;
    workout_id: number | null;
    name: string;
    sets?: number;
    reps?: number;
    duration?: string;
    notes?: string;
    order_in_day: number;
    type?: 'superset';
    exercises?: Array<{
      workout_id: number;
      name: string;
      reps: number;
      sets: number;
      rest_seconds: number;
    }>;
  }>>>({});

  const [selectedDayForWorkout, setSelectedDayForWorkout] = useState<number | null>(null);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [isCreatingSuperset, setIsCreatingSuperset] = useState(false);
  const [supersetExercises, setSupersetExercises] = useState<Array<{
    workout_id: number;
    name: string;
    reps: number;
    sets: number;
    rest_seconds: number;
  }>>([]);
  const [workoutSearchTerm, setWorkoutSearchTerm] = useState('');
  const [editingProgramWorkout, setEditingProgramWorkout] = useState<{day: number, workoutId: string} | null>(null);
  const [showError, setShowError] = useState<string | null>(null);
  const [editingProgram, setEditingProgram] = useState<WorkoutProgram | null>(null);
  const [editingProgramWorkouts, setEditingProgramWorkouts] = useState<Record<number, Array<{
    id: string;
    workout_id: number | null;
    name: string;
    sets?: number;
    reps?: number;
    duration?: string;
    notes?: string;
    order_in_day: number;
    type?: 'superset';
    exercises?: Array<{
      workout_id: number;
      name: string;
      reps: number;
      sets: number;
      rest_seconds: number;
    }>;
  }>>>({});

  const loadProgramWorkouts = async (programId: number) => {
    try {
      // Load the specific program with its workouts from the API
      const programDetails = await apiService.getWorkoutProgram(programId);
      
      // Convert the program workouts to the editing format
      const workoutsByDay: Record<number, Array<{
        id: string;
        workout_id: number | null;
        name: string;
        sets?: number;
        reps?: number;
        duration?: string;
        notes?: string;
        order_in_day: number;
        type?: 'superset';
        exercises?: Array<{
          workout_id: number;
          name: string;
          reps: number;
          sets: number;
          rest_seconds: number;
        }>;
      }>> = {};

      if (programDetails && programDetails.workouts) {
        programDetails.workouts.forEach((workout: any) => {
          const day = workout.day_of_week;
          if (!workoutsByDay[day]) {
            workoutsByDay[day] = [];
          }
          
          // Regular workout
          workoutsByDay[day].push({
            id: workout.id.toString(),
            workout_id: workout.workout_id,
            name: workout.workout_name || 'Unknown Workout',
            sets: workout.sets,
            reps: workout.reps,
            duration: workout.duration,
            notes: workout.notes,
            order_in_day: workout.order_in_day,
            type: workout.type,
            exercises: workout.exercises
          });
        });
      }

      // Process supersets if they exist
      if (programDetails && programDetails.supersets) {
        console.log('🔍 Processing supersets from API:', programDetails.supersets);
        programDetails.supersets.forEach((superset: any) => {
          const day = superset.day_of_week;
          if (!workoutsByDay[day]) {
            workoutsByDay[day] = [];
          }
          
          // Convert superset exercises to the expected format
          const exercises = superset.exercises.map((ex: any) => ({
            workout_id: ex.workout_id,
            name: ex.workout_name || 'Unknown Exercise',
            reps: ex.reps || 8,
            sets: ex.sets || 3,
            rest_seconds: ex.rest_seconds || 60
          }));
          
          workoutsByDay[day].push({
            id: `superset_${superset.id}`,
            workout_id: null,
            name: superset.name,
            sets: superset.sets,
            reps: superset.reps,
            duration: superset.duration,
            notes: superset.notes,
            order_in_day: superset.order_in_day,
            type: 'superset',
            exercises: exercises
          });
        });
      }
      
      setEditingProgramWorkouts(workoutsByDay);
    } catch (error) {
      console.error('Failed to load program workouts:', error);
      setShowError('Failed to load program workouts');
    }
  };


  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [workoutsData, categoriesData, programsData] = await Promise.all([
        apiService.getWorkouts(),
        apiService.getWorkoutCategories(),
        apiService.getWorkoutPrograms()
      ]);
      setWorkouts(workoutsData);
      setCategories(categoriesData);
      setPrograms(programsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check for query parameters to open modals
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      setShowCreateWorkout(true);
      setActiveTab('workouts');
      // Remove the query parameter from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const filteredWorkouts = workouts.filter(workout => {
    const matchesSearch = workout.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workout.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === null || workout.category_id === selectedCategory;
    return matchesSearch && matchesCategory && workout.is_active;
  });

  const handleCreateWorkout = async () => {
    try {
      const workoutData = {
        ...newWorkout,
        category_id: newWorkout.category_id || undefined,
        duration_minutes: newWorkout.duration_minutes || undefined
      };
      await apiService.createWorkout(workoutData);
      setNewWorkout({
        name: '',
        description: '',
        instructions: '',
        category_id: null,
        duration_minutes: null,
        equipment_needed: [],
        muscle_groups: [],
        video_url: '',
        image_url: ''
      });
      setShowCreateWorkout(false);
      loadData();
    } catch (error) {
      console.error('Error creating workout:', error);
    }
  };

  const handleUpdateWorkout = async () => {
    if (!editingWorkout) return;
    try {
      await apiService.updateWorkout(editingWorkout.id, editingWorkout);
      setEditingWorkout(null);
      loadData();
    } catch (error) {
      console.error('Error updating workout:', error);
    }
  };

  const handleDeleteWorkout = async (id: number) => {
    try {
      await apiService.deleteWorkout(id);
      loadData();
    } catch (error) {
      console.error('Error deleting workout:', error);
    }
  };

  const handleCreateCategory = async () => {
    try {
      await apiService.createWorkoutCategory(newCategory);
      setNewCategory({ name: '', color: '#6366f1' });
      setShowCreateCategory(false);
      loadData();
    } catch (error) {
      console.error('Error creating category:', error);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await apiService.deleteWorkoutCategory(id);
      loadData();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const addWorkoutToDay = (workout: Workout, day: number) => {
    const isEditing = editingProgram !== null;
    const dayWorkouts = isEditing ? (editingProgramWorkouts[day] || []) : (programWorkouts[day] || []);
    const newWorkout = {
      id: `workout-${Date.now()}`,
        workout_id: workout.id,
        name: workout.name,
        sets: 3,
      reps: 8,
      duration: '60s',
      notes: '',
      order_in_day: dayWorkouts.length + 1
    };
    
    if (isEditing) {
      setEditingProgramWorkouts({
        ...editingProgramWorkouts,
        [day]: [...dayWorkouts, newWorkout]
      });
    } else {
      setProgramWorkouts({
        ...programWorkouts,
        [day]: [...dayWorkouts, newWorkout]
      });
    }
    setShowExerciseSelector(false);
    setSelectedDayForWorkout(null);
    setWorkoutSearchTerm('');
  };

  const removeWorkoutFromDay = (day: number, workoutId: string) => {
    const isEditing = editingProgram !== null;
    const dayWorkouts = isEditing ? (editingProgramWorkouts[day] || []) : (programWorkouts[day] || []);
    const updatedWorkouts = dayWorkouts.filter(w => w.id !== workoutId);
    
    if (isEditing) {
      setEditingProgramWorkouts({
        ...editingProgramWorkouts,
        [day]: updatedWorkouts
      });
    } else {
      setProgramWorkouts({
        ...programWorkouts,
        [day]: updatedWorkouts
      });
    }
  };

  const updateWorkoutDetails = (day: number, workoutId: string, updates: {sets?: number, reps?: number}) => {
    const isEditing = editingProgram !== null;
    const dayWorkouts = isEditing ? (editingProgramWorkouts[day] || []) : (programWorkouts[day] || []);
    const updatedWorkouts = dayWorkouts.map(w => 
      w.id === workoutId ? { ...w, ...updates } : w
    );
    
    if (isEditing) {
      setEditingProgramWorkouts({
        ...editingProgramWorkouts,
        [day]: updatedWorkouts
      });
    } else {
      setProgramWorkouts({
        ...programWorkouts,
        [day]: updatedWorkouts
      });
    }
    setEditingProgramWorkout(null);
  };


  const getDayName = (dayNumber: number) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayNumber - 1];
  };

  const addSupersetToProgram = (day: number) => {
    console.log('🚀 addSupersetToProgram called for day:', day);
    console.log('📊 supersetExercises:', supersetExercises);
    console.log('✏️ isEditing:', editingProgram !== null);
    
    if (supersetExercises.length < 2) {
      console.log('❌ Not enough exercises for superset');
      return;
    }
    
    const isEditing = editingProgram !== null;
    const dayWorkouts = isEditing ? (editingProgramWorkouts[day] || []) : (programWorkouts[day] || []);
    
    const superset = {
      id: `superset_${Date.now()}`,
      workout_id: null,
      name: `Superset: ${supersetExercises.map(ex => ex.name).join(', ')}`,
      type: 'superset' as const,
      exercises: [...supersetExercises],
      sets: supersetExercises[0]?.sets || 3,
      reps: supersetExercises[0]?.reps || 8,
      duration: '60s',
      notes: '',
      order_in_day: dayWorkouts.length + 1
    };
    
    console.log('🎯 Created superset:', superset);
    
    if (isEditing) {
      console.log('✏️ Adding superset to editingProgramWorkouts for day:', day);
      setEditingProgramWorkouts({
        ...editingProgramWorkouts,
        [day]: [...dayWorkouts, superset]
      });
    } else {
      console.log('➕ Adding superset to programWorkouts for day:', day);
      setProgramWorkouts({
        ...programWorkouts,
        [day]: [...dayWorkouts, superset]
      });
    }
    setSupersetExercises([]);
    setIsCreatingSuperset(false);
    setShowExerciseSelector(false);
    setSelectedDayForWorkout(null);
    setWorkoutSearchTerm('');
  };

  const addWorkoutToSuperset = (workout: Workout) => {
    const newExercise = {
      workout_id: workout.id,
      name: workout.name,
      reps: 10,
      sets: 3,
      rest_seconds: 60
    };
    setSupersetExercises([...supersetExercises, newExercise]);
  };

  const removeWorkoutFromSuperset = (index: number) => {
    setSupersetExercises(supersetExercises.filter((_, idx) => idx !== index));
  };

  const handleCreateProgram = async () => {
    try {
      // Validate required fields
      if (!newProgram.name.trim()) {
        setShowError('Please enter a program name');
        return;
      }

      const apiWorkouts: Array<{
        workout_id: number;
        day_of_week: number;
        week_number: number;
        order_in_day: number;
        sets?: number;
        reps?: number;
        duration?: string;
        notes?: string;
      }> = [];

      Object.entries(programWorkouts).forEach(([dayStr, dayWorkouts]) => {
        const day = parseInt(dayStr);
        dayWorkouts.forEach((workout, index) => {
          if (workout.workout_id && workout.workout_id > 0) {
            // Handle regular workouts
            apiWorkouts.push({
              workout_id: workout.workout_id,
              day_of_week: day,
              week_number: 1,
              order_in_day: workout.order_in_day,
              sets: workout.sets,
              reps: workout.reps,
              duration: workout.duration,
              notes: workout.notes
            });
          }
        });
      });

      // Check if we have any valid workouts
      if (apiWorkouts.length === 0) {
        setShowError('Please add at least one workout to the program');
        return;
      }

      const programData = {
        name: newProgram.name.trim(),
        description: newProgram.description?.trim() || '',
        duration_weeks: newProgram.duration_weeks,
        workouts: apiWorkouts
      };
      
      const createdProgram = await apiService.createWorkoutProgram(programData);
      
      // Create supersets separately using proper API
      const supersetsToCreate: any[] = [];
      Object.entries(programWorkouts).forEach(([dayStr, dayWorkouts]) => {
        const day = parseInt(dayStr);
        dayWorkouts.forEach((workout) => {
          if (workout.type === 'superset' && workout.exercises) {
            supersetsToCreate.push({
              name: workout.name,
              day_of_week: day,
              week_number: 1,
              order_in_day: workout.order_in_day || 1,
              sets: workout.sets || 3,
              reps: workout.reps || 8,
              duration: workout.duration || '60s',
              notes: workout.notes || '',
              exercises: workout.exercises
            });
          }
        });
      });

      // Create all supersets
      for (const supersetData of supersetsToCreate) {
        try {
          await apiService.createSuperset({
            ...supersetData,
            program_id: createdProgram.id
          });
        } catch (error) {
          console.error('Error creating superset:', error);
        }
      }

      setNewProgram({ name: '', description: '', duration_weeks: 4 });
      setProgramWorkouts({});
      setShowCreateProgram(false);
      setShowError(null);
      loadData();
    } catch (error) {
      console.error('Error creating program:', error);
    }
  };

  const handleDeleteProgram = async (id: number) => {
    try {
      console.log('Deleting program with ID:', id);
      await apiService.deleteWorkoutProgram(id);
      console.log('Program deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirmData) return;
    
    try {
      console.log('Confirming delete for:', deleteConfirmData);
      if (deleteConfirmData.type === 'workout') {
        await handleDeleteWorkout(deleteConfirmData.id);
      } else if (deleteConfirmData.type === 'category') {
        await handleDeleteCategory(deleteConfirmData.id);
      } else if (deleteConfirmData.type === 'program') {
        await handleDeleteProgram(deleteConfirmData.id);
      }
      setShowDeleteConfirm(false);
      setDeleteConfirmData(null);
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  if (loading) {
  return (
      <div className="flex items-center justify-center py-12">
        <div className={clsx(
          "text-xl transition-colors",
          theme === 'dark' ? "text-white" : "text-gray-900"
        )}>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className={clsx(
          "text-4xl font-bold bg-clip-text text-transparent",
          theme === 'dark'
            ? "bg-gradient-to-r from-white to-gray-300"
            : "bg-gradient-to-r from-gray-900 to-gray-700"
        )}>
          Workouts
        </h1>
        <p className={clsx(
          "mt-3 text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          Manage your workout library and programs
        </p>
      </div>

        {/* Tab Navigation */}
        <TabNavigation
          tabs={[
            {
              id: 'workouts',
              label: 'Workouts',
              icon: <FireIcon className="h-5 w-5" />
            },
            {
              id: 'programs',
              label: 'Programs',
              icon: <RectangleStackIcon className="h-5 w-5" />
            }
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as 'workouts' | 'categories' | 'programs')}
        />

        {/* Tab Content */}
        {activeTab === 'workouts' && (
          <div>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 mb-8">
          <button
                onClick={() => setShowCreateWorkout(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Create Workout</span>
          </button>
          <button
                onClick={() => setShowManageCategories(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <CogIcon className="h-5 w-5" />
                <span>Manage Categories</span>
          </button>
      </div>
      
            {/* Search and Filter */}
            <div className={clsx(
              "backdrop-blur-sm rounded-2xl p-6 mb-8 border transition-colors",
              theme === 'dark'
                ? "bg-white/5 border-white/10"
                : "bg-white border-gray-200"
            )}>
              <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className={clsx(
                "h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )} />
              <input
                type="text"
                placeholder="Search workouts..."
                    className={clsx(
                      "w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-300"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                    )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
                  className={clsx(
                    "px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 appearance-none cursor-pointer transition-colors",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  )}
            >
              <option value="">All Categories</option>
                  {categories.filter(cat => cat.is_active).map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
              ))}
            </select>
              </div>
          </div>

          {/* Workouts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredWorkouts.map((workout) => (
                <div key={workout.id} className={clsx(
                  "backdrop-blur-sm rounded-2xl p-6 border transition-all duration-200",
                  theme === 'dark'
                    ? "bg-white/5 border-white/10 hover:border-white/20"
                    : "bg-white border-gray-200 hover:border-gray-300"
                )}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      {workout.category_name && (
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: workout.category_color || '#6366f1' }}
                    ></div>
                      )}
                      <h3 className={clsx(
                        "text-xl font-semibold transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>{workout.name}</h3>
                </div>
                <div className="flex space-x-2">
                  <button
                        onClick={() => setEditingWorkout(workout)}
                        className={clsx(
                          "p-2 transition-colors",
                          theme === 'dark'
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                        )}
                        title="Edit workout"
                      >
                        <PencilIcon className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => {
                          setDeleteConfirmData({ type: 'workout', id: workout.id, name: workout.name });
                          setShowDeleteConfirm(true);
                    }}
                        className={clsx(
                          "p-2 transition-colors",
                          theme === 'dark'
                            ? "text-gray-400 hover:text-red-400"
                            : "text-gray-500 hover:text-red-600"
                        )}
                        title="Delete workout"
                  >
                        <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
                  
                  {workout.description && (
                    <p className={clsx(
                      "mb-4 line-clamp-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>{workout.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <div className="flex items-center space-x-4">
                      {workout.duration_minutes && (
                        <span className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {workout.duration_minutes} min
                        </span>
                      )}
                      {workout.category_name && (
                        <span className="flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-1" />
                          {workout.category_name}
                        </span>
            )}
          </div>
                    <div className="flex items-center space-x-2">
                      {workout.equipment_needed && workout.equipment_needed.length > 0 && (
                        <span className="px-2 py-1 bg-indigo-600/20 text-indigo-300 rounded-md text-xs">
                          {workout.equipment_needed.length} equipment
                        </span>
                      )}
                      {workout.muscle_groups && workout.muscle_groups.length > 0 && (
                        <span className="px-2 py-1 bg-green-600/20 text-green-300 rounded-md text-xs">
                          {workout.muscle_groups.length} muscles
                        </span>
                      )}
          </div>
              </div>
              </div>
              ))}
                </div>
          </div>
        )}


        {activeTab === 'programs' && (
          <div>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 mb-8">
              <button
                onClick={() => setShowCreateProgram(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Create Program</span>
              </button>
                </div>

            {/* Programs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {programs.map((program) => (
                <div key={program.id} className={clsx(
                  "backdrop-blur-sm rounded-2xl p-6 border transition-all duration-200",
                  theme === 'dark'
                    ? "bg-white/5 border-white/10 hover:border-white/20"
                    : "bg-white border-gray-200 hover:border-gray-300"
                )}>
                  <div className="flex justify-between items-start mb-4">
                    <h3 className={clsx(
                      "text-xl font-semibold transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>{program.name}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                          setEditingProgram(program);
                          loadProgramWorkouts(program.id);
                    }}
                        className={clsx(
                          "p-2 transition-colors",
                          theme === 'dark' ? "text-gray-400 hover:text-blue-400" : "text-gray-500 hover:text-blue-600"
                        )}
                        title="Edit Program"
                  >
                        <PencilIcon className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => {
                          setDeleteConfirmData({ type: 'program', id: program.id, name: program.name });
                          setShowDeleteConfirm(true);
                    }}
                        className={clsx(
                          "p-2 transition-colors",
                          theme === 'dark' ? "text-gray-400 hover:text-red-400" : "text-gray-500 hover:text-red-600"
                        )}
                        title="Delete Program"
                  >
                        <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
                  
                  {program.description && (
                    <p className={clsx(
                      "mb-4 line-clamp-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>{program.description}</p>
            )}
                  
                  <div className={clsx(
                    "flex items-center text-sm transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>
                    <ChartBarIcon className="h-4 w-4 mr-2" />
                    {program.duration_weeks} weeks
          </div>
                </div>
              ))}
            </div>
          </div>
      )}

        {/* All Modals */}
      {/* Create Workout Modal */}
      {showCreateWorkout && (
          <div 
            className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteConfirm(false);
                setDeleteConfirmData(null);
              }
            }}
          >
            <div 
              className="bg-gray-800 rounded-2xl p-8 w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Create New Workout</h3>
              <button
                onClick={() => setShowCreateWorkout(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
              
            <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Workout Name</label>
                  <input
                    type="text"
                    value={newWorkout.name}
                    onChange={(e) => setNewWorkout({ ...newWorkout, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    placeholder="Enter workout name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={newWorkout.description}
                    onChange={(e) => setNewWorkout({ ...newWorkout, description: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    rows={3}
                    placeholder="Enter workout description"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select
                    value={newWorkout.category_id || ''}
                      onChange={(e) => setNewWorkout({ ...newWorkout, category_id: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  >
                    <option value="">Select Category</option>
                      {categories.filter(cat => cat.is_active).map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                  
              <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Duration (minutes)</label>
                    <input
                      type="number"
                      value={newWorkout.duration_minutes || ''}
                      onChange={(e) => setNewWorkout({ ...newWorkout, duration_minutes: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                      placeholder="Duration in minutes"
                />
              </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Video URL</label>
                  <input
                    type="url"
                    value={newWorkout.video_url}
                    onChange={(e) => setNewWorkout({ ...newWorkout, video_url: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    placeholder="https://example.com/video.mp4"
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateWorkout(false)}
                    className="px-6 py-3 border border-gray-600 rounded-xl text-white hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateWorkout}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors"
                >
                  Create Workout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* View Workout Modal */}
        {viewingWorkout && (
          <div 
            className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteConfirm(false);
                setDeleteConfirmData(null);
              }
            }}
          >
            <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Workout Details</h3>
              <button
                  onClick={() => setViewingWorkout(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
              
              <div className="space-y-6">
              <div>
                  <h4 className="text-lg font-semibold text-white mb-2">{viewingWorkout.name}</h4>
                  {viewingWorkout.category_name && (
                    <div className="flex items-center space-x-2 mb-4">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: viewingWorkout.category_color || '#6366f1' }}
                      ></div>
                      <span className="text-gray-300">{viewingWorkout.category_name}</span>
              </div>
                  )}
              </div>
                
                {viewingWorkout.description && (
              <div>
                    <h5 className="text-md font-medium text-gray-300 mb-2">Description</h5>
                    <p className="text-gray-400">{viewingWorkout.description}</p>
        </div>
      )}

                {viewingWorkout.instructions && (
              <div>
                    <h5 className="text-md font-medium text-gray-300 mb-2">Instructions</h5>
                    <p className="text-gray-400">{viewingWorkout.instructions}</p>
                </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {viewingWorkout.duration_minutes && (
                <div>
                      <h5 className="text-md font-medium text-gray-300 mb-2">Duration</h5>
                      <p className="text-gray-400">{viewingWorkout.duration_minutes} minutes</p>
                </div>
              )}

                  {viewingWorkout.equipment_needed && viewingWorkout.equipment_needed.length > 0 && (
                <div>
                      <h5 className="text-md font-medium text-gray-300 mb-2">Equipment Needed</h5>
                  <div className="flex flex-wrap gap-2">
                        {viewingWorkout.equipment_needed.map((equipment, index) => (
                          <span key={index} className="px-2 py-1 bg-indigo-600/20 text-indigo-300 rounded-md text-sm">
                        {equipment}
                      </span>
                    ))}
                  </div>
                </div>
              )}
                </div>

                {viewingWorkout.muscle_groups && viewingWorkout.muscle_groups.length > 0 && (
                <div>
                    <h5 className="text-md font-medium text-gray-300 mb-2">Muscle Groups</h5>
                  <div className="flex flex-wrap gap-2">
                      {viewingWorkout.muscle_groups.map((muscle, index) => (
                        <span key={index} className="px-2 py-1 bg-green-600/20 text-green-300 rounded-md text-sm">
                        {muscle}
                      </span>
                    ))}
                  </div>
                </div>
              )}

                {viewingWorkout.video_url && (
                <div>
                    <h5 className="text-md font-medium text-gray-300 mb-2">Video</h5>
                    <div className="relative w-full h-64 bg-gray-700 rounded-lg overflow-hidden">
                      <video 
                        controls 
                        className="w-full h-full object-cover"
                        src={viewingWorkout.video_url}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                </div>
              )}

                <div className="flex justify-end pt-4">
                <button
                    onClick={() => setViewingWorkout(null)}
                    className="px-6 py-3 border border-gray-600 rounded-xl text-white hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Workout Modal */}
        {editingWorkout && (
          <div 
            className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteConfirm(false);
                setDeleteConfirmData(null);
              }
            }}
          >
            <div 
              className="bg-gray-800 rounded-2xl p-8 w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Edit Workout</h3>
              <button
                  onClick={() => setEditingWorkout(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
              
            <div className="space-y-4">
              <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Workout Name</label>
                <input
                  type="text"
                    value={editingWorkout?.name || ''}
                    onChange={(e) => setEditingWorkout({ ...editingWorkout!, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
                
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                    value={editingWorkout?.description || ''}
                    onChange={(e) => setEditingWorkout({ ...editingWorkout!, description: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Video URL</label>
                <input
                  type="url"
                    value={editingWorkout?.video_url || ''}
                    onChange={(e) => setEditingWorkout({ ...editingWorkout!, video_url: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    placeholder="https://example.com/video.mp4"
                />
              </div>
                
              <div className="flex justify-end space-x-3">
                <button
                    onClick={() => setEditingWorkout(null)}
                    className="px-6 py-3 border border-gray-600 rounded-xl text-white hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                    onClick={handleUpdateWorkout}
                    className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors"
                >
                    Update Workout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Create Category Modal */}
        {showCreateCategory && (
          <div 
            className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteConfirm(false);
                setDeleteConfirmData(null);
              }
            }}
          >
            <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Create New Category</h3>
              <button
                  onClick={() => setShowCreateCategory(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
              
              <div className="space-y-4">
              <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category Name</label>
                  <input
                    type="text"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    placeholder="Enter category name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Color</label>
                  <input
                    type="color"
                    value={newCategory.color}
                    onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                    className="w-full h-12 bg-white/10 border border-white/20 rounded-xl"
                  />
              </div>

              <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCreateCategory(false)}
                    className="px-6 py-3 border border-gray-600 rounded-xl text-white hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateCategory}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors"
                  >
                    Create Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manage Categories Modal */}
        {showManageCategories && (
          <div 
            className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteConfirm(false);
                setDeleteConfirmData(null);
              }
            }}
          >
            <div className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl border w-full max-w-4xl max-h-[80vh] overflow-y-auto transition-colors",
              theme === 'dark'
                ? "bg-slate-800/95 border-slate-700/50"
                : "bg-white/95 border-gray-200/50"
            )}>
              <div className={clsx(
                "flex justify-between items-center mb-6 p-6 border-b transition-colors",
                theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-2xl font-bold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>Manage Categories</h3>
                <button
                  onClick={() => setShowManageCategories(false)}
                  className={clsx(
                    "transition-colors",
                    theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                  )}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Create New Category Section */}
                <div className={clsx(
                  "rounded-lg p-4 transition-colors",
                  theme === 'dark' ? "bg-white/5" : "bg-gray-50"
                )}>
                  <h4 className={clsx(
                    "text-lg font-semibold mb-4 transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Create New Category</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={clsx(
                        "block text-sm font-medium mb-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>Category Name</label>
                      <input
                        type="text"
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                        className={clsx(
                          "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors",
                          theme === 'dark'
                            ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                        placeholder="Enter category name"
                      />
                    </div>
                    <div>
                      <label className={clsx(
                        "block text-sm font-medium mb-2 transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-700"
                      )}>Color</label>
                      <input
                        type="color"
                        value={newCategory.color}
                        onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                        className={clsx(
                          "w-full h-10 border rounded-md transition-colors",
                          theme === 'dark' ? "border-white/20" : "border-gray-300"
                        )}
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleCreateCategory}
                        className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors"
                      >
                        Create Category
                      </button>
                    </div>
                  </div>
                </div>

                {/* Existing Categories */}
                <div>
                  <h4 className={clsx(
                    "text-lg font-semibold mb-4 transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Existing Categories</h4>
                  <div className="space-y-2">
                    {categories.filter(cat => cat.is_active).map((category) => (
                      <div key={category.id} className={clsx(
                        "flex items-center justify-between p-4 rounded-lg border transition-colors",
                        theme === 'dark'
                          ? "bg-white/5 border-white/10"
                          : "bg-white border-gray-200"
                      )}>
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: category.color }}
                          ></div>
                          <span className={clsx(
                            "font-medium transition-colors",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>{category.name}</span>
                          <span className={clsx(
                            "text-sm transition-colors",
                            theme === 'dark' ? "text-gray-400" : "text-gray-500"
                          )}>
                            ({workouts.filter(w => w.category_id === category.id).length} workouts)
                          </span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingCategory(category)}
                            className={clsx(
                              "p-2 transition-colors",
                              theme === 'dark'
                                ? "text-gray-400 hover:text-indigo-400"
                                : "text-gray-500 hover:text-indigo-600"
                            )}
                            title="Edit category"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                <button
                  onClick={() => {
                              setDeleteConfirmData({ type: 'category', id: category.id, name: category.name });
                              setShowDeleteConfirm(true);
                  }}
                            className={clsx(
                              "p-2 transition-colors",
                              theme === 'dark'
                                ? "text-gray-400 hover:text-red-400"
                                : "text-gray-500 hover:text-red-600"
                            )}
                            title="Delete category"
                >
                            <TrashIcon className="h-4 w-4" />
                </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                <button
                    onClick={() => setShowManageCategories(false)}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Edit Category Modal */}
        {editingCategory && (
          <div 
            className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDeleteConfirm(false);
                setDeleteConfirmData(null);
              }
            }}
          >
            <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Edit Category</h3>
              <button
                  onClick={() => setEditingCategory(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
              
            <div className="space-y-4">
              <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category Name</label>
                <input
                  type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
                
              <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Color</label>
                  <input
                    type="color"
                    value={editingCategory.color}
                    onChange={(e) => setEditingCategory({ ...editingCategory, color: e.target.value })}
                    className="w-full h-12 bg-white/10 border border-white/20 rounded-xl"
                />
              </div>
                
              <div className="flex justify-end space-x-3">
                <button
                    onClick={() => setEditingCategory(null)}
                    className="px-6 py-3 border border-gray-600 rounded-xl text-white hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                    onClick={async () => {
                      try {
                        await apiService.updateWorkoutCategory(editingCategory.id, editingCategory);
                        setEditingCategory(null);
                        loadData();
                      } catch (error) {
                        console.error('Error updating category:', error);
                      }
                    }}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-colors"
                  >
                    Update Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Create Program Modal - Weekly Schedule Builder */}
      {showCreateProgram && (
          <div className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-y-auto border transition-colors",
              theme === 'dark'
                ? "bg-slate-800/95 border-slate-700/50"
                : "bg-white/95 border-gray-200/50"
            )}>
              <div className={clsx(
                "flex justify-between items-center p-8 border-b transition-colors",
                theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
              )}>
                <h3 className={clsx(
                  "text-3xl font-bold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>Create New Program</h3>
              <button
                onClick={() => {
                  setShowCreateProgram(false);
                    setProgramWorkouts({});
                    setSelectedDayForWorkout(null);
                  setIsCreatingSuperset(false);
                  setSupersetExercises([]);
                }}
                className={clsx(
                  "transition-colors",
                  theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                )}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
              <div className="p-8 space-y-8">
              {/* Program Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                    <label className={clsx(
                      "block text-lg font-medium mb-3 transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>Program Name</label>
                  <input
                    type="text"
                    value={newProgram.name}
                    onChange={(e) => setNewProgram({ ...newProgram, name: e.target.value })}
                      className={clsx(
                        "w-full px-6 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-lg transition-colors",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      )}
                    placeholder="Enter program name"
                  />
                </div>
                <div>
                    <label className="block text-lg font-medium text-white mb-3">Duration (weeks)</label>
                  <input
                    type="number"
                    value={newProgram.duration_weeks}
                    onChange={(e) => setNewProgram({ ...newProgram, duration_weeks: parseInt(e.target.value) || 4 })}
                      className={clsx(
                        "w-full px-6 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-lg transition-colors",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      )}
                    min="1"
                    max="52"
                  />
                </div>
              </div>
              
              <div>
                  <label className={clsx(
                    "block text-lg font-medium mb-3 transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Description</label>
                  <textarea
                    value={newProgram.description}
                    onChange={(e) => setNewProgram({ ...newProgram, description: e.target.value })}
                    className={clsx(
                      "w-full px-6 py-4 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 text-lg transition-colors",
                      theme === 'dark'
                        ? "bg-white/10 border-white/20 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    )}
                    rows={4}
                    placeholder="Enter program description"
                  />
              </div>

                {/* Weekly Schedule Builder */}
                <div className="space-y-6">
                  <h4 className={clsx(
                    "text-2xl font-semibold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Weekly Schedule</h4>
                  <div className="grid grid-cols-1 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <div key={day} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 min-h-[200px]">
                        {/* Header with day name and action buttons */}
                <div className="flex justify-between items-center mb-4">
                          <h5 className="font-semibold text-white text-lg">{getDayName(day)}</h5>
                  <div className="flex space-x-2">
                    <button
                              onClick={() => {
                                setSelectedDayForWorkout(day);
                                setShowExerciseSelector(true);
                                setIsCreatingSuperset(false);
                              }}
                              className="px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg font-medium"
                            >
                              + Add Workout
                    </button>
                    <button
                              onClick={() => {
                                setSelectedDayForWorkout(day);
                                setShowExerciseSelector(true);
                                setIsCreatingSuperset(true);
                              }}
                              className="px-3 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white text-xs rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg font-medium"
                            >
                              + Add Superset
                    </button>
                  </div>
                </div>

                        {/* Workouts area */}
                        <div className="space-y-3 flex-1">
                          {(programWorkouts[day] || []).length > 0 ? (
                            (programWorkouts[day] || []).map((workout, index) => (
                              <div key={workout.id} className="bg-white/10 rounded-lg border border-white/20 p-3">
                                <div className="flex justify-between items-start mb-2">
                                  <span className="text-white font-medium text-sm truncate">{workout.name}</span>
                                  <div className="flex space-x-1">
                      <button
                                      onClick={() => setEditingProgramWorkout({day, workoutId: workout.id})}
                                      className="text-blue-400 hover:text-blue-300 text-sm"
                                      title="Edit sets/reps"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => removeWorkoutFromDay(day, workout.id)}
                                      className="text-red-400 hover:text-red-300 text-lg font-bold"
                                    >
                                      ×
                      </button>
                                  </div>
                    </div>
                    
                                {editingProgramWorkout?.day === day && editingProgramWorkout?.workoutId === workout.id ? (
                                  <div className="space-y-3 mt-3 pt-3 border-t border-white/10">
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className={clsx(
                                          "block text-sm font-medium mb-2 transition-colors",
                                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                                        )}>Sets</label>
                      <input
                                          type="number"
                                          value={workout.sets}
                                          onChange={(e) => updateWorkoutDetails(day, workout.id, {sets: parseInt(e.target.value) || 1})}
                                          className={clsx(
                                            "w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors",
                                            theme === 'dark'
                                              ? "bg-white/10 border border-white/20 text-white"
                                              : "bg-gray-50 border border-gray-300 text-gray-900"
                                          )}
                                          min="1"
                                          max="20"
                      />
                    </div>
                                      <div>
                                        <label className={clsx(
                                          "block text-sm font-medium mb-2 transition-colors",
                                          theme === 'dark' ? "text-gray-300" : "text-gray-700"
                                        )}>Reps</label>
                                        <input
                                          type="number"
                                          value={workout.reps}
                                          onChange={(e) => updateWorkoutDetails(day, workout.id, {reps: parseInt(e.target.value) || 1})}
                                          className={clsx(
                                            "w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors",
                                            theme === 'dark'
                                              ? "bg-white/10 border border-white/20 text-white"
                                              : "bg-gray-50 border border-gray-300 text-gray-900"
                                          )}
                                          min="1"
                                          max="100"
                                        />
                                      </div>
                                    </div>
                                    <div className="flex justify-end space-x-2">
                                      <button
                                        onClick={() => setEditingProgramWorkout(null)}
                                        className={clsx(
                                          "px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all duration-200",
                                          theme === 'dark'
                                            ? "bg-orange-500 hover:bg-orange-600 text-white"
                                            : "bg-orange-500 hover:bg-orange-600 text-white"
                                        )}
                                      >
                                        Done
                                      </button>
                                    </div>
                            </div>
                                ) : (
                                  <div className="text-gray-300 text-xs">
                                    {workout.type === 'superset' ? (
                                      <div>
                                        <div>{workout.exercises?.length} exercises</div>
                                        <div>{workout.sets} sets × {workout.reps} reps</div>
                          </div>
                                    ) : (
                                      <div>{workout.sets} sets × {workout.reps} reps</div>
                                    )}
                            </div>
                                )}
                            </div>
                            ))
                          ) : (
                            <div className="flex items-center justify-center h-24 text-gray-400 text-sm">
                              <div className="text-center">
                                <div>No workouts added yet</div>
                                <div className="text-xs mt-1">Click the buttons above to add workouts</div>
                              </div>
                            </div>
                          )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-white/10 px-8 pb-8">
                      <button
                        onClick={() => {
                      setShowCreateProgram(false);
                      setProgramWorkouts({});
                      setSelectedDayForWorkout(null);
                          setIsCreatingSuperset(false);
                          setSupersetExercises([]);
                        }}
                    className="px-6 py-3 border border-white/20 rounded-xl text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all duration-200"
                      >
                    Cancel
                      </button>
                            <button
                    onClick={handleCreateProgram}
                    disabled={Object.keys(programWorkouts).length === 0}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 disabled:transform-none shadow-lg"
                            >
                    Create Program
                            </button>
                          </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Program Modal - Full Weekly Schedule Builder */}
        {editingProgram && (
          <div 
            className="fixed inset-0 lg:left-64 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setEditingProgram(null);
                setEditingProgramWorkouts({});
                setEditingProgramWorkout(null);
              }
            }}
          >
            <div className={clsx(
              "backdrop-blur-lg rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-y-auto border transition-colors",
              theme === 'dark'
                ? "bg-slate-800/95 border-slate-700/50"
                : "bg-white/95 border-gray-200/50"
            )}>
              <div className={clsx(
                "p-8 border-b transition-colors",
                theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
              )}>
                <h2 className={clsx(
                  "text-3xl font-bold transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>Edit Program</h2>
                <p className={clsx(
                  "mt-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-600"
                )}>Modify your workout program details and schedule</p>
              </div>

              <div className="p-8 space-y-6">
                {/* Program Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>Program Name</label>
                              <input
                      type="text"
                      value={editingProgram.name}
                      onChange={(e) => setEditingProgram({...editingProgram, name: e.target.value})}
                      className={clsx(
                        "w-full px-4 py-3 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-colors",
                        theme === 'dark'
                          ? "bg-white/5 border border-white/20 text-white"
                          : "bg-gray-50 border border-gray-300 text-gray-900"
                      )}
                      placeholder="Enter program name"
                              />
                            </div>
                  
                            <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>Duration (weeks)</label>
                              <input
                                type="number"
                                min="1"
                      max="52"
                      value={editingProgram.duration_weeks}
                      onChange={(e) => setEditingProgram({...editingProgram, duration_weeks: parseInt(e.target.value) || 1})}
                      className={clsx(
                        "w-full px-4 py-3 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent transition-colors",
                        theme === 'dark'
                          ? "bg-white/5 border border-white/20 text-white"
                          : "bg-gray-50 border border-gray-300 text-gray-900"
                      )}
                              />
                            </div>

                            <div>
                    <label className={clsx(
                      "block text-sm font-medium mb-2 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-700"
                    )}>Program ID</label>
                    <div className={clsx(
                      "w-full px-4 py-3 rounded-xl transition-colors",
                      theme === 'dark'
                        ? "bg-white/5 border border-white/20 text-gray-400"
                        : "bg-gray-50 border border-gray-300 text-gray-600"
                    )}>
                      #{editingProgram.id}
                            </div>
                          </div>
                        </div>

                <div>
                  <label className={clsx(
                    "block text-sm font-medium mb-2 transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-700"
                  )}>Description</label>
                  <textarea
                    value={editingProgram.description || ''}
                    onChange={(e) => setEditingProgram({...editingProgram, description: e.target.value})}
                    rows={3}
                    className={clsx(
                      "w-full px-4 py-3 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-transparent resize-none transition-colors",
                      theme === 'dark'
                        ? "bg-white/5 border border-white/20 text-white"
                        : "bg-gray-50 border border-gray-300 text-gray-900"
                    )}
                    placeholder="Enter program description"
                  />
                    </div>

                {/* Weekly Schedule Builder */}
                <div>
                  <h3 className={clsx(
                    "text-xl font-semibold mb-4 transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>Weekly Schedule Builder</h3>
                  <p className={clsx(
                    "text-sm mb-6 transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-600"
                  )}>
                    Drag and drop workouts to different days, or click "Add Workout" to add new exercises.
                  </p>
                  
                  <div className="grid grid-cols-1 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                      <div key={day} className={clsx(
                        "backdrop-blur-sm rounded-xl p-6 border min-h-[200px] transition-colors",
                        theme === 'dark'
                          ? "bg-white/5 border-white/10"
                          : "bg-gray-50 border-gray-200"
                      )}>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className={clsx(
                            "text-lg font-semibold transition-colors",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>
                            {getDayName(day)}
                          </h4>
                          <div className="flex space-x-2">
                      <button
                              onClick={() => {
                                setSelectedDayForWorkout(day);
                                setIsCreatingSuperset(false);
                                setShowExerciseSelector(true);
                              }}
                              className="px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200"
                            >
                              Add Workout
                            </button>
                            <button
                              onClick={() => {
                                setSelectedDayForWorkout(day);
                                setIsCreatingSuperset(true);
                                setShowExerciseSelector(true);
                              }}
                              className="px-3 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg transition-all duration-200"
                            >
                              Add Superset
                      </button>
                          </div>
                    </div>

                        <div className="space-y-3">
                          {editingProgramWorkouts[day] && editingProgramWorkouts[day].length > 0 ? (
                            editingProgramWorkouts[day]
                              .sort((a, b) => (a.order_in_day || 0) - (b.order_in_day || 0))
                              .map((workout, index) => (
                              <div key={workout.id} className={clsx(
                                "rounded-lg border p-3 transition-colors",
                                theme === 'dark'
                                  ? "bg-white/10 border-white/20"
                                  : "bg-white border-gray-200"
                              )}>
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h5 className={clsx(
                                      "font-medium transition-colors",
                                      theme === 'dark' ? "text-white" : "text-gray-900"
                                    )}>{workout.name}</h5>
                                    {workout.type === 'superset' && workout.exercises && (
                                      <p className={clsx(
                                        "text-sm mt-1 transition-colors",
                                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                                      )}>
                                        {workout.exercises.map(ex => ex.name).join(', ')}
                                      </p>
                                    )}
                                    {editingProgramWorkout?.day === day && editingProgramWorkout?.workoutId === workout.id ? (
                                      <div className="space-y-3 mt-3 pt-3 border-t border-white/10">
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <label className={clsx(
                                              "block text-sm font-medium mb-2 transition-colors",
                                              theme === 'dark' ? "text-gray-300" : "text-gray-700"
                                            )}>Sets</label>
                                            <input
                                              type="number"
                                              value={workout.sets || ''}
                                              onChange={(e) => updateWorkoutDetails(day, workout.id, {sets: parseInt(e.target.value) || 1})}
                                              className={clsx(
                                                "w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors",
                                                theme === 'dark'
                                                  ? "bg-white/10 border border-white/20 text-white"
                                                  : "bg-gray-50 border border-gray-300 text-gray-900"
                                              )}
                                              min="1"
                                              max="20"
                                            />
                                          </div>
                                          <div>
                                            <label className={clsx(
                                              "block text-sm font-medium mb-2 transition-colors",
                                              theme === 'dark' ? "text-gray-300" : "text-gray-700"
                                            )}>Reps</label>
                                            <input
                                              type="number"
                                              value={workout.reps || ''}
                                              onChange={(e) => updateWorkoutDetails(day, workout.id, {reps: parseInt(e.target.value) || 1})}
                                              className={clsx(
                                                "w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors",
                                                theme === 'dark'
                                                  ? "bg-white/10 border border-white/20 text-white"
                                                  : "bg-gray-50 border border-gray-300 text-gray-900"
                                              )}
                                              min="1"
                                              max="100"
                                            />
                                          </div>
                                        </div>
                                        <div className="flex justify-end space-x-2">
                                          <button
                                            onClick={() => setEditingProgramWorkout(null)}
                                            className={clsx(
                                              "px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all duration-200",
                                              theme === 'dark'
                                                ? "bg-orange-500 hover:bg-orange-600 text-white"
                                                : "bg-orange-500 hover:bg-orange-600 text-white"
                                            )}
                                          >
                                            Done
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className={clsx(
                                        "flex space-x-4 mt-2 text-sm transition-colors",
                                        theme === 'dark' ? "text-gray-400" : "text-gray-600"
                                      )}>
                                        {workout.sets && <span>Sets: {workout.sets}</span>}
                                        {workout.reps && <span>Reps: {workout.reps}</span>}
                                        {workout.duration && <span>Duration: {workout.duration}</span>}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex space-x-2">
                      <button
                                      onClick={() => setEditingProgramWorkout({day, workoutId: workout.id})}
                                      className={clsx(
                                        "p-1 transition-colors",
                                        theme === 'dark' ? "text-gray-400 hover:text-blue-400" : "text-gray-500 hover:text-blue-600"
                                      )}
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => removeWorkoutFromDay(day, workout.id)}
                                      className={clsx(
                                        "p-1 transition-colors",
                                        theme === 'dark' ? "text-gray-400 hover:text-red-400" : "text-gray-500 hover:text-red-600"
                                      )}
                                    >
                                      <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className={clsx(
                              "text-center py-8 transition-colors",
                              theme === 'dark' ? "text-gray-400" : "text-gray-500"
                            )}>
                              <p className="text-sm">No workouts added yet</p>
                              <p className="text-xs mt-1">Click "Add Workout" or "Add Superset" to get started</p>
                  </div>
                )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className={clsx(
                "flex justify-end space-x-3 pt-6 border-t px-8 pb-8 transition-colors",
                theme === 'dark' ? "border-white/10" : "border-gray-200"
              )}>
                <button
                  onClick={() => {
                    setEditingProgram(null);
                    setEditingProgramWorkouts({});
                    setProgramWorkouts({});
                    setSelectedDayForWorkout(null);
                    setIsCreatingSuperset(false);
                    setSupersetExercises([]);
                    setEditingProgramWorkout(null);
                  }}
                  className={clsx(
                    "px-6 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "border-white/20 text-white hover:bg-white/10"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  )}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      // Convert editingProgramWorkouts to API format
                      const apiWorkouts: Array<{
                        workout_id: number;
                        day_of_week: number;
                        week_number: number;
                        order_in_day: number;
                        sets?: number;
                        reps?: number;
                        duration?: string;
                        notes?: string;
                      }> = [];

                      Object.entries(editingProgramWorkouts).forEach(([dayStr, dayWorkouts]) => {
                        const day = parseInt(dayStr);
                        dayWorkouts.forEach((workout, index) => {
                          if (workout.workout_id && workout.workout_id > 0) {
                            // Handle regular workouts
                            apiWorkouts.push({
                              workout_id: workout.workout_id,
                              day_of_week: day,
                              week_number: 1,
                              order_in_day: workout.order_in_day,
                              sets: workout.sets,
                              reps: workout.reps,
                              duration: workout.duration,
                              notes: workout.notes
                            });
                          }
                        });
                      });

                      // Collect supersets for the program
                      const supersetsToUpdate: any[] = [];
                      console.log('🔍 Collecting supersets from editingProgramWorkouts:', editingProgramWorkouts);
                      Object.entries(editingProgramWorkouts).forEach(([dayStr, dayWorkouts]) => {
                        const day = parseInt(dayStr);
                        console.log(`📅 Day ${day} workouts:`, dayWorkouts);
                        dayWorkouts.forEach((workout) => {
                          console.log('🏋️ Checking workout:', workout.name, 'type:', workout.type);
                          if (workout.type === 'superset' && workout.exercises) {
                            console.log('✅ Found superset:', workout.name);
                            supersetsToUpdate.push({
                              name: workout.name,
                              day_of_week: day,
                              week_number: 1,
                              order_in_day: workout.order_in_day || 1,
                              sets: workout.sets || 3,
                              reps: workout.reps || 8,
                              duration: workout.duration || '60s',
                              notes: workout.notes || '',
                              exercises: workout.exercises.map((ex: any) => ({
                                workout_id: ex.workout_id,
                                sets: ex.sets || 3,
                                reps: ex.reps || 8,
                                rest_seconds: ex.rest_seconds || 60
                              }))
                            });
                          }
                        });
                      });
                      console.log('🎯 Final supersetsToUpdate:', supersetsToUpdate);

                      const programData = {
                        name: editingProgram.name,
                        description: editingProgram.description || '',
                        duration_weeks: editingProgram.duration_weeks,
                        workouts: apiWorkouts,
                        supersets: supersetsToUpdate
                      };

                      await apiService.updateWorkoutProgram(editingProgram.id, programData);
                      setEditingProgram(null);
                      setEditingProgramWorkouts({});
                      setShowError(null);
                      loadData();
                    } catch (error) {
                      console.error('Error updating program:', error);
                      setShowError('Failed to update program');
                    }
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Update Program
                </button>
                        </div>
            </div>
          </div>
        )}

        {/* Exercise Selection Modal */}
        {showExerciseSelector && selectedDayForWorkout && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000]">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 w-full max-w-5xl max-h-[80vh] overflow-y-auto shadow-2xl border border-white/20">
              <div className="flex justify-between items-center mb-6">
                <h5 className="text-2xl font-semibold text-white">
                  {isCreatingSuperset ? 'Create Superset for' : 'Add Workout to'} {getDayName(selectedDayForWorkout)}
                </h5>
                        <button
                  onClick={() => {
                    setShowExerciseSelector(false);
                    setSelectedDayForWorkout(null);
                    setIsCreatingSuperset(false);
                    setSupersetExercises([]);
                    setWorkoutSearchTerm('');
                  }}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                    type="text"
                    placeholder="Search workouts..."
                    value={workoutSearchTerm}
                    onChange={(e) => setWorkoutSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                            />
                          </div>
              </div>
              
              {isCreatingSuperset ? (
                <div className="space-y-4">
                  <div className="text-gray-300">
                    <p className="mb-4">Add exercises to create a superset:</p>
                    {supersetExercises.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {supersetExercises.map((exercise, index) => (
                          <div key={index} className="flex items-center justify-between bg-white/10 rounded-lg p-3">
                            <span className="text-white">{exercise.name}</span>
                            <div className="flex items-center space-x-2">
                            <input
                              type="number"
                                value={exercise.sets}
                                onChange={(e) => {
                                  const newExercises = [...supersetExercises];
                                  newExercises[index].sets = parseInt(e.target.value) || 0;
                                  setSupersetExercises(newExercises);
                                }}
                                className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                                placeholder="Sets"
                              />
                            <input
                              type="number"
                                value={exercise.reps}
                                onChange={(e) => {
                                  const newExercises = [...supersetExercises];
                                  newExercises[index].reps = parseInt(e.target.value) || 0;
                                  setSupersetExercises(newExercises);
                                }}
                                className="w-16 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                                placeholder="Reps"
                              />
                              <button
                                onClick={() => removeWorkoutFromSuperset(index)}
                                className="text-red-400 hover:text-red-300"
                              >
                                ×
                              </button>
                          </div>
                        </div>
                        ))}
                              </div>
                    )}
                  </div>
                  
                  <div className="space-y-4">
                    {/* Reps and Sets Inputs */}
                    <div className="grid grid-cols-2 gap-4">
                                <div>
                        <label className="block text-sm font-medium text-white mb-2">Sets</label>
                                  <input
                                    type="number"
                          value={supersetExercises.length > 0 ? supersetExercises[0].sets : 3}
                                    onChange={(e) => {
                            const newSets = parseInt(e.target.value) || 3;
                            setSupersetExercises(prev => prev.map(ex => ({ ...ex, sets: newSets })));
                          }}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                                    min="1"
                          max="20"
                                  />
                                </div>
                                <div>
                        <label className="block text-sm font-medium text-white mb-2">Reps</label>
                                  <input
                                    type="number"
                          value={supersetExercises.length > 0 ? supersetExercises[0].reps : 10}
                                    onChange={(e) => {
                            const newReps = parseInt(e.target.value) || 10;
                            setSupersetExercises(prev => prev.map(ex => ({ ...ex, reps: newReps })));
                          }}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                                    min="1"
                          max="100"
                                  />
                                </div>
                                </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-64 overflow-y-auto">
                      {workouts
                        .filter(workout => 
                          workout.name.toLowerCase().includes(workoutSearchTerm.toLowerCase())
                        )
                        .map((workout) => (
                        <button
                          key={workout.id}
                          onClick={() => addWorkoutToSuperset(workout)}
                          className="p-3 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-colors"
                        >
                          <div className="text-white font-medium">{workout.name}</div>
                          {workout.description && (
                            <div className="text-gray-400 text-sm mt-1 line-clamp-2">{workout.description}</div>
                          )}
                        </button>
                      ))}
                              </div>
                            </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => {
                        setShowExerciseSelector(false);
                        setSelectedDayForWorkout(null);
                        setIsCreatingSuperset(false);
                        setSupersetExercises([]);
                      }}
                      className="px-4 py-2 border border-gray-600 rounded-lg text-white hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => addSupersetToProgram(selectedDayForWorkout)}
                      disabled={supersetExercises.length < 2}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      Add Superset
                    </button>
                        </div>
                    </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-64 overflow-y-auto">
                    {workouts
                      .filter(workout => 
                        workout.name.toLowerCase().includes(workoutSearchTerm.toLowerCase())
                      )
                      .map((workout) => (
                      <button
                        key={workout.id}
                        onClick={() => addWorkoutToDay(workout, selectedDayForWorkout)}
                        className="p-3 bg-white/5 hover:bg-white/10 rounded-lg text-left transition-colors"
                      >
                        <div className="text-white font-medium">{workout.name}</div>
                        {workout.description && (
                          <div className="text-gray-400 text-sm mt-1 line-clamp-2">{workout.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={() => {
                        setShowExerciseSelector(false);
                        setSelectedDayForWorkout(null);
                      }}
                      className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-medium"
                    >
                      Close
                    </button>
                  </div>
                    </div>
                  )}
                </div>
              </div>
        )}

        {/* Custom Delete Confirmation Modal */}
        {showDeleteConfirm && deleteConfirmData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Confirm Delete</h3>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete "{deleteConfirmData.name}"? This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
                >
                  Delete
                </button>
            </div>
          </div>
        </div>
      )}

        {/* Error Notification */}
        {showError && (
          <div className="fixed top-20 right-4 z-[99999] bg-red-600 text-white px-6 py-4 rounded-lg shadow-2xl border border-red-500 max-w-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{showError}</span>
              <button
                onClick={() => setShowError(null)}
                className="ml-4 text-white hover:text-gray-200 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default WorkoutsPage;
